import { decode } from 'base64-arraybuffer';
import * as ImageManipulator from 'expo-image-manipulator';
import * as ImagePicker from 'expo-image-picker';

import { supabase } from '@/lib/supabase';

// ── Tags ─────────────────────────────────────────────────────────────────────

export type DisputeTag = { key: string; label: string };

export const DISPUTE_TAGS: DisputeTag[] = [
  { key: 'did_not_deliver',          label: 'Δεν παρέδωσε' },
  { key: 'did_not_pay',              label: 'Δεν πλήρωσε' },
  { key: 'completion_denied',        label: 'Αρνείται να επιβεβαιώσει ολοκλήρωση' },
  { key: 'payment_denied',           label: 'Αρνείται να επιβεβαιώσει εξόφληση' },
  { key: 'unilateral_terms_change',  label: 'Άλλαξε όρους μονομερώς' },
  { key: 'no_response',              label: 'Δεν ανταποκρίνεται' },
  { key: 'other',                    label: 'Άλλο' },
];

export function disputeTagLabel(key: string): string {
  return DISPUTE_TAGS.find((t) => t.key === key)?.label ?? key;
}

// ── Types ─────────────────────────────────────────────────────────────────────

export type DisputeStatus =
  | 'open' | 'resolved' | 'withdrawn' | 'auto_cancelled' | 'mutual_resolution_proposed'
  | 'voting' | 'admin_review' | 'jury_review';

export type DisputeInfo = {
  id: string;
  agreement_id: string;
  initiated_by: string;
  accused_user_id: string | null;
  status: DisputeStatus;
  respondent_decision: 'accepted' | 'contested' | null;
  outcome:
    | 'withdrawn' | 'penalty_respondent' | 'penalty_respondent_auto' | 'mutual_resolution' | 'deadlock'
    | 'jury_favor_initiator' | 'jury_favor_accused' | 'jury_no_quorum' | null;
  expires_at: string;
  resolved_at: string | null;
  communication_declared_at: string | null;
  mutual_resolution_expires_at: string | null;
  mutual_resolution_proposed_by_me: boolean;
  i_am_initiator: boolean;
  i_have_submitted: boolean;
  my_tags: string[];
  my_statement: string | null;
  my_evidence_urls: string[];
  both_submitted: boolean;
  other_tags: string[] | null;
  other_statement: string | null;
  other_evidence_urls: string[] | null;
  my_penalty: number | null;
  other_penalty: number | null;
  /** Ενέχυρο (Ink) — Stake & Jury System. */
  stake_amount: number;
  stake_locked: boolean;
  voting_expires_at: string | null;
  jury_size: number;
  jury_votes_cast: number;
  malicious_flagged: boolean;
};

/** Ανώνυμη «κάρτα» στατιστικών ενός συμβαλλόμενου — ΧΩΡΙΣ όνομα/φωτογραφία. */
export type JuryProfileStats = {
  completed_count: number;
  cancelled_unilateral_count: number;
  effective_rating: number;
  review_count: number;
  disputes_opened_count: number;
  disputes_won_count: number;
  dispute_win_rate_flagged: boolean;
} | null;

/** Ανώνυμη υπόθεση προς κρίση — μόνο τίτλος συμφωνίας + tags + ανώνυμα στατιστικά, ποτέ ονόματα/IDs μερών. */
export type JuryDuty = {
  dispute_id: string;
  agreement_title: string;
  initiator_tags: string[];
  initiator_statement: string | null;
  initiator_evidence_urls: string[] | null;
  accused_tags: string[];
  accused_statement: string | null;
  accused_evidence_urls: string[] | null;
  stake_amount: number;
  jury_size: number;
  voting_expires_at: string;
  /** Deadline ΓΙΑ ΤΟΝ ΣΥΓΚΕΚΡΙΜΕΝΟ κριτή (12ω από την ανάθεση) — αν δεν ψηφίσει
   * ως τότε, αντικαθίσταται τυχαία (βλ. replace_unresponsive_jurors). */
  my_vote_deadline: string;
  already_voted: boolean;
  initiator_stats: JuryProfileStats;
  accused_stats: JuryProfileStats;
};

// ── API ───────────────────────────────────────────────────────────────────────

export async function getDisputeForAgreement(agreementId: string): Promise<DisputeInfo | null> {
  const { data, error } = await supabase.rpc('get_dispute_for_agreement', {
    p_agreement_id: agreementId,
  });
  if (error) throw error;
  return data as DisputeInfo | null;
}

export async function openDispute(agreementId: string, tags: string[], statement?: string, evidenceUrls?: string[]): Promise<string> {
  const { data, error } = await supabase.rpc('open_dispute', {
    p_agreement_id: agreementId,
    p_tags: tags,
    p_statement: statement?.trim() || null,
    p_evidence_urls: evidenceUrls ?? null,
  });
  if (error) throw error;
  return (data as { dispute_id: string }).dispute_id;
}

export async function acceptDispute(disputeId: string): Promise<void> {
  const { error } = await supabase.rpc('accept_dispute', { p_dispute_id: disputeId });
  if (error) throw error;
}

export async function contestDispute(disputeId: string, tags: string[], statement?: string, evidenceUrls?: string[]): Promise<void> {
  const { error } = await supabase.rpc('contest_dispute', {
    p_dispute_id: disputeId,
    p_tags: tags,
    p_statement: statement?.trim() || null,
    p_evidence_urls: evidenceUrls ?? null,
  });
  if (error) throw error;
}

/** Επιλέγει, συμπιέζει και ανεβάζει ένα screenshot/φωτογραφία ως αποδεικτικό
 * διαφωνίας σε private bucket — επιστρέφει το storage path (όχι δημόσιο URL,
 * το bucket είναι ιδιωτικό). Χρησιμοποίησε {@link getDisputeEvidenceSignedUrl}
 * για να το εμφανίσεις. Μπορεί να ανέβει πριν καν υπάρξει η διαφωνία (ο χρήστης
 * το επισυνάπτει ενώ γράφει ακόμα τη δήλωσή του) — η ορατότητα στον άλλο
 * συμβαλλόμενο ελέγχεται από το αν το path καταλήξει στο evidence_urls της
 * δήλωσης, όχι από το όνομα του φακέλου. */
// Μέγιστη πλευρά εικόνας evidence — αρκετό για να διαβαστεί καθαρά μια
// φωτογραφία δουλειάς/ζημιάς, χωρίς να κρατάμε πολύ βαριά αρχεία σε bucket
// που έτσι κι αλλιώς διαγράφεται 12 μήνες μετά την επίλυση.
const MAX_EVIDENCE_SIDE = 800;

async function resizeAndUploadEvidence(userId: string, uri: string, width: number, height: number): Promise<string> {
  const longestSide = Math.max(width, height);
  const scale = Math.min(1, MAX_EVIDENCE_SIDE / longestSide);
  const targetWidth = Math.round(width * scale);
  const targetHeight = Math.round(height * scale);

  const manipulated = await ImageManipulator.manipulateAsync(
    uri,
    [{ resize: { width: targetWidth, height: targetHeight } }],
    { compress: 0.6, format: ImageManipulator.SaveFormat.JPEG, base64: true },
  );

  const path = `${userId}/${Date.now()}-${Math.floor(Math.random() * 1e6)}.jpg`;
  const { error: upErr } = await supabase.storage
    .from('dispute-evidence')
    .upload(path, decode(manipulated.base64!), { contentType: 'image/jpeg', cacheControl: '3600' });
  if (upErr) throw upErr;
  return path;
}

export async function pickAndUploadDisputeEvidence(userId: string): Promise<string | null> {
  // Χωρίς requestMediaLibraryPermissionsAsync() — βλ. σχόλιο στο avatar.ts.
  const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Images, quality: 1 });
  const asset = result.assets?.[0];
  if (result.canceled || !asset?.uri) return null;
  return resizeAndUploadEvidence(userId, asset.uri, asset.width ?? MAX_EVIDENCE_SIDE, asset.height ?? MAX_EVIDENCE_SIDE);
}

/** Λήψη φωτογραφίας απευθείας από την κάμερα (π.χ. τεκμηρίωση ζημιάς/δουλειάς
 * επί τόπου) — ίδιο resize/upload pipeline με την επιλογή από τη συλλογή. */
export async function captureAndUploadDisputeEvidence(userId: string): Promise<string | null> {
  const perm = await ImagePicker.requestCameraPermissionsAsync();
  if (!perm.granted) throw new Error('Χρειάζεται άδεια πρόσβασης στην κάμερα.');
  const result = await ImagePicker.launchCameraAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Images, quality: 1 });
  const asset = result.assets?.[0];
  if (result.canceled || !asset?.uri) return null;
  return resizeAndUploadEvidence(userId, asset.uri, asset.width ?? MAX_EVIDENCE_SIDE, asset.height ?? MAX_EVIDENCE_SIDE);
}

export async function getDisputeEvidenceSignedUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from('dispute-evidence')
    .createSignedUrl(path, 3600);
  if (error) throw error;
  return data.signedUrl;
}

export async function withdrawDispute(disputeId: string): Promise<void> {
  const { error } = await supabase.rpc('withdraw_dispute', { p_dispute_id: disputeId });
  if (error) throw error;
}

export async function persistDispute(disputeId: string): Promise<void> {
  const { error } = await supabase.rpc('persist_dispute', { p_dispute_id: disputeId });
  if (error) throw error;
}

export async function declareCommunication(disputeId: string): Promise<void> {
  const { error } = await supabase.rpc('declare_communication', { p_dispute_id: disputeId });
  if (error) throw error;
}

export async function proposeMutualResolution(disputeId: string): Promise<void> {
  const { error } = await supabase.rpc('propose_mutual_resolution', { p_dispute_id: disputeId });
  if (error) throw error;
}

export async function respondMutualResolution(disputeId: string, accept: boolean): Promise<void> {
  const { error } = await supabase.rpc('respond_mutual_resolution', {
    p_dispute_id: disputeId,
    p_accept: accept,
  });
  if (error) throw error;
}

// ── Stake & Jury System ──────────────────────────────────────────────────────

/** Ανώνυμες υποθέσεις όπου ο τρέχων χρήστης είναι κληρωμένος κριτής και εκκρεμεί η ψήφος του. */
export async function getMyJuryDuties(): Promise<JuryDuty[]> {
  const { data, error } = await supabase.rpc('get_my_jury_duties' as any);
  if (error) throw error;
  return ((data ?? []) as unknown) as JuryDuty[];
}

/** Ψήφος κριτή: true = πιστεύω τον initiator (ο κατηγορούμενος φταίει), false = πιστεύω τον κατηγορούμενο. */
export async function castJuryVote(disputeId: string, voteForInitiator: boolean): Promise<void> {
  const { error } = await supabase.rpc('cast_jury_vote' as any, {
    p_dispute_id: disputeId,
    p_vote_for_initiator: voteForInitiator,
  });
  if (error) throw error;
}

/** Ανώνυμη συνομιλία συμφωνίας για κριτή — μόνο side ('initiator'/'accused'/'other') + κείμενο, ποτέ ονόματα. */
export type JuryDisputeMessage = { side: 'initiator' | 'accused' | 'other'; body: string; created_at: string; read_by_recipient: boolean | null };

export async function getJuryDisputeMessages(disputeId: string): Promise<JuryDisputeMessage[]> {
  const { data, error } = await supabase.rpc('get_jury_dispute_messages' as any, { p_dispute_id: disputeId });
  if (error) throw error;
  return ((data ?? []) as unknown) as JuryDisputeMessage[];
}

// ── Dispute outcome entries for profiles ──────────────────────────────────────

export type DisputeOutcomeEntry = {
  dispute_id: string;
  agreement_id: string;
  agreement_title: string;
  outcome: 'accepted' | 'penalty_respondent' | 'auto_cancelled' | string;
  initiator_id: string;
  initiator_name: string;
  initiator_tags: string[];
  respondent_id: string;
  respondent_name: string;
  respondent_tags: string[];
  resolved_at: string | null;
};

export async function fetchDisputesForProfile(userId: string): Promise<DisputeOutcomeEntry[]> {
  const { data, error } = await supabase.rpc('get_disputes_for_profile' as any, { p_user_id: userId });
  if (error) throw error;
  return ((data ?? []) as unknown) as DisputeOutcomeEntry[];
}
